import { useEffect, useRef, useState } from "react";

/**
 * usePullToRefresh — mobile-only pull-down-to-refresh gesture.
 *
 * Attach by spreading the returned `bind` props onto your page's root
 * scroll element (the body scrolls by default in this app, so we listen
 * on `window`). The hook returns visual state (`pullDistance`, `phase`,
 * `isRefreshing`) for a small indicator component to render.
 *
 * Behaviour:
 *  - Active only when window.scrollY === 0 AND the user starts a touch
 *    drag downward (positive deltaY).
 *  - Distance is rubber-banded (1/2.5 of raw delta) and capped at MAX_PULL.
 *  - Releasing past THRESHOLD triggers `onRefresh()` (awaited promise).
 *  - Releasing before THRESHOLD snaps back without firing.
 *  - Pointer-only fallback (mouse) is intentionally NOT supported — this
 *    is purely mobile UX. Desktop users use F5.
 *
 * @param {() => Promise<void>|void} onRefresh - callback when user pulls past threshold
 * @param {object} opts
 * @param {boolean} opts.enabled - turn the listener on/off (default: true)
 * @param {number} opts.threshold - px needed to trigger refresh (default: 70)
 * @param {number} opts.maxPull - max visual pull distance in px (default: 110)
 */
const THRESHOLD = 70;
const MAX_PULL = 110;
const RESISTANCE = 2.5;

export default function usePullToRefresh(onRefresh, opts = {}) {
  const enabled = opts.enabled !== false;
  const threshold = opts.threshold ?? THRESHOLD;
  const maxPull = opts.maxPull ?? MAX_PULL;

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Mutable touch tracking — kept in refs so re-renders don't reset mid-gesture.
  const startY = useRef(null);
  const tracking = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    // Only mount on touch-capable devices to keep desktop noise-free.
    const isTouch = typeof window !== "undefined" && "ontouchstart" in window;
    if (!isTouch) return;

    const onTouchStart = (e) => {
      if (isRefreshing) return;
      if (window.scrollY > 0) return;
      startY.current = e.touches[0].clientY;
      tracking.current = true;
    };

    const onTouchMove = (e) => {
      if (!tracking.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        // User is scrolling up (negative) — abort tracking, normal scroll.
        tracking.current = false;
        setPullDistance(0);
        return;
      }
      // Active pull
      const next = Math.min(dy / RESISTANCE, maxPull);
      setPullDistance(next);
      // Prevent rubber-band fight on iOS once we've engaged
      if (next > 8 && e.cancelable) {
        e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      if (!tracking.current) return;
      tracking.current = false;
      const distance = pullDistance;
      startY.current = null;
      if (distance >= threshold && typeof onRefresh === "function") {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, onRefresh, threshold, maxPull, pullDistance, isRefreshing]);

  const phase =
    isRefreshing ? "refreshing" : pullDistance >= threshold ? "ready" : pullDistance > 0 ? "pulling" : "idle";

  return { pullDistance, isRefreshing, phase, threshold };
}
